/**
 * /dashboard/settings/payment-accounts — Settings → Accounting →
 * Payment accounts CRUD (SOP §8 + §11.6).
 *
 * Exposes the full wallet lifecycle at the Settings surface:
 *  - "New wallet" button → PaymentAccountForm dialog (create mode)
 *  - PaymentAccountList table with per-row Edit / Archive actions
 *  - RestoreDefaultsButton for the seed catalog (Cash + Bank — Primary)
 *
 * Mirrors the Money hub pattern from accounting/money/index.tsx but lives
 * under Settings so non-accounting-module users can manage their payment
 * accounts without navigating into the Accounting module.
 *
 * Migration note (2026-06-05, feature-completion): extended from the
 * stub that only had RestoreDefaultsButton — now full CRUD.
 */

import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useHrRoles } from "@/components/hr/use-hr-roles";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { RestoreDefaultsButton } from "@/components/settings/restore-defaults-button";
import { PaymentAccountList } from "@/components/accounting/payment-account-list";
import { PaymentAccountForm } from "@/components/accounting/payment-account-form";
import type { PaymentAccount } from "@/types/accounting";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/payment-accounts",
)({
  component: SettingsPaymentAccountsRoute,
});

function SettingsPaymentAccountsRoute() {
  const { isHrAdmin } = useHrRoles();

  const [walletDialogOpen, setWalletDialogOpen] = useState(false);
  const [editingWallet, setEditingWallet] = useState<PaymentAccount | undefined>(
    undefined,
  );

  function openCreate() {
    setEditingWallet(undefined);
    setWalletDialogOpen(true);
  }

  function openEdit(pa: PaymentAccount) {
    setEditingWallet(pa);
    setWalletDialogOpen(true);
  }

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Payment Accounts</h2>
              <p className="text-sm text-muted-foreground">
                Cash + bank accounts used by Accounting receipts / payments.
              </p>
            </div>
            {isHrAdmin && (
              <Button
                onClick={openCreate}
                className="gap-1.5"
                data-testid="money-hub-new-wallet"
              >
                <Plus aria-hidden className="h-4 w-4" />
                New Bank / Cash Account
              </Button>
            )}
          </div>

          {!isHrAdmin && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only HR admins / accountants can manage payment accounts.
              </AlertDescription>
            </Alert>
          )}

          {/* Wallet CRUD list — edit/archive actions only shown to admins */}
          <PaymentAccountList onEdit={isHrAdmin ? openEdit : undefined} />

          {/* Default catalog restore */}
          {isHrAdmin && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Default Catalog</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col items-start gap-3">
                <p className="text-sm text-muted-foreground">
                  Adds a Cash account + a placeholder &quot;Bank —
                  Primary&quot; account. Both can be renamed to your actual
                  entity once seeded.
                </p>
                <RestoreDefaultsButton
                  catalog="payment-accounts"
                  catalogLabel="payment accounts"
                  invalidateKeys={[["accounting", "payment-accounts"]]}
                />
              </CardContent>
            </Card>
          )}
        </div>
      </SettingsShell>

      <PaymentAccountForm
        open={walletDialogOpen}
        onOpenChange={setWalletDialogOpen}
        account={editingWallet}
      />
    </>
  );
}
